import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Card,
  Col,
  Form,
  Popconfirm,
  Row,
  Space,
  Typography,
  UploadFile,
  message,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  DeleteOutlined,
  EditOutlined,
  KeyOutlined,
  LeftCircleOutlined,
  LockOutlined,
  SaveOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { RcFile, UploadChangeParam } from 'antd/lib/upload/interface';
import { AButton, AUpload, ContainerForm } from '../../../../components';
import { ContainerRow } from './styles';
import { useToast } from '../../../../hooks/toast';
import {
  IProfessional,
  IProfessionalSignature,
} from '../../../../shared/interfaces';
import { schemaProfessionalSignature } from '../../schemas/yup.professional.schema';
import ResetSignaturePassword from './ResetSignaturePassword';
import {
  pathProfissionalSignature,
  pathProfessionalClearSignature,
} from '../../../../http';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const SignatureCanvasModule = require('react-signature-canvas');

const SignatureCanvas = SignatureCanvasModule.default || SignatureCanvasModule;

interface IProfessionalSignatureProps {
  professional: IProfessional | null;
}

const ProfessionalSignature: React.FC<IProfessionalSignatureProps> = ({
  professional,
}) => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const sigPadRef = useRef<any>(null);
  const [password, setPassword] = useState<string>();
  const [confirmPassword, setConfirmPassword] = useState<string>();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [modalReset, setModalReset] = useState<boolean>(false);
  const [signatureUploaded, setSignatureUploaded] = useState<
    string | ArrayBuffer | null
  >(null);
  const [a1PfxBase64, setA1PfxBase64] = useState<string>('');
  const [a1CertificatePassword, setA1CertificatePassword] =
    useState<string>('');

  const clear = useCallback(() => {
    sigPadRef.current?.clear();
  }, []);

  const handleSignature = useCallback(async () => {
    setDisabled(true);
    try {
      const signatureCanvasIsEmpty = sigPadRef.current?.isEmpty() ?? true;

      if (signatureCanvasIsEmpty && !signatureUploaded) {
        throw new Error(
          'É necessário fornecer uma assinatura (desenhada ou por upload).',
        );
      }

      const signature =
        signatureUploaded ??
        sigPadRef.current?.getTrimmedCanvas().toDataURL('image/png');

      const formValidated: IProfessionalSignature =
        await schemaProfessionalSignature.validate(
          {
            signature,
            signature_password: password,
            confirmPassword,
            ...(a1PfxBase64
              ? {
                  a1_pfx_base64: a1PfxBase64,
                  a1_certificate_password: a1CertificatePassword,
                }
              : {}),
          },
          { abortEarly: false },
        );

      if (a1PfxBase64 && !a1CertificatePassword) {
        throw new Error('Informe a senha do certificado A1 para concluir.');
      }

      delete formValidated.confirmPassword;

      await pathProfissionalSignature(professional?.id, formValidated);

      addToast({
        type: 'info',
        title: 'Assinatura salva',
        description: 'Assinatura salva com sucesso.',
      });
      setTimeout(() => navigate(-1), 1100);
    } catch (err: any) {
      const errorMessage =
        err.errors?.[0] ||
        err.response?.data?.error ||
        'Ocorreu um erro inesperado.';
      addToast({
        type: 'error',
        title: 'Erro ao salvar',
        description: errorMessage,
      });
    } finally {
      setDisabled(false);
    }
  }, [
    professional?.id,
    password,
    confirmPassword,
    signatureUploaded,
    addToast,
    navigate,
  ]);

  const onChange = useCallback((info: UploadChangeParam<UploadFile<any>>) => {
    if (info.file.status === 'removed') {
      setSignatureUploaded(null);
      return;
    }

    if (info.file.size && info.file.size > 100000) {
      message.error('Arquivo excede o tamanho limite de 100kb');
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(info.file.originFileObj as RcFile);
    reader.onloadend = () => setSignatureUploaded(reader.result);
  }, []);

  const beforeUpload = useCallback((file: RcFile) => {
    const isPNG = file.type === 'image/png';
    if (!isPNG) message.error(`${file.name} não é um arquivo do tipo PNG`);
    return false;
  }, []);

  const onChangeA1 = useCallback((info: UploadChangeParam<UploadFile<any>>) => {
    if (info.file.status === 'removed') {
      setA1PfxBase64('');
      return;
    }

    if (info.file.size && info.file.size > 5 * 1024 * 1024) {
      message.error('Arquivo A1 excede o tamanho limite de 5MB');
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(info.file.originFileObj as RcFile);
    reader.onloadend = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      setA1PfxBase64(base64 || '');
    };
  }, []);

  const beforeUploadA1 = useCallback((file: RcFile) => {
    const lower = file.name.toLowerCase();
    const isA1File = lower.endsWith('.pfx') || lower.endsWith('.p12');
    if (!isA1File)
      message.error(`${file.name} não é um arquivo do tipo .pfx ou .p12`);
    return false;
  }, []);

  const handleClearSignature = useCallback(async () => {
    setDisabled(true);
    try {
      await pathProfessionalClearSignature(professional?.id);

      addToast({
        type: 'info',
        title: 'Assinatura removida',
        description:
          'Assinatura e credenciais removidas. Documentos já assinados não são alterados.',
      });

      setSignatureUploaded(null);
      setPassword(undefined);
      setConfirmPassword(undefined);
      clear();
      setTimeout(() => navigate(-1), 1100);
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro ao remover assinatura',
        description:
          err.response?.data?.error ||
          'Não foi possível remover a assinatura no momento.',
      });
    } finally {
      setDisabled(false);
    }
  }, [professional?.id, addToast, clear, navigate]);

  const renderModalResetPassword = useCallback(() => {
    if (modalReset)
      return (
        <ResetSignaturePassword
          open={modalReset}
          close={() => setModalReset(false)}
          professional={professional}
        />
      );
  }, [modalReset, professional]);

  const renderSignatureCanvas = useCallback(() => {
    if (!signatureUploaded)
      return (
        <Card
          size="small"
          title={
            <Space size={6}>
              <EditOutlined style={{ color: '#1677ff' }} />
              <span>Desenhe sua assinatura abaixo</span>
            </Space>
          }
          style={{ marginBottom: 12 }}
          extra={
            <AButton
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={clear}
            >
              Limpar
            </AButton>
          }
        >
          <SignatureCanvas
            ref={sigPadRef}
            penColor="black"
            velocityFilterWeight={0.9}
            canvasProps={{
              className: 'sigCanvas',
              style: {
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                width: '100%',
                height: 240,
                background: '#fafafa',
              },
            }}
          />
        </Card>
      );
  }, [signatureUploaded, clear]);

  useEffect(() => {
    if (professional?.signature && sigPadRef.current) {
      sigPadRef.current.fromDataURL(professional.signature);
    }
  }, [professional]);

  return (
    <ContainerForm>
      <Row style={{ marginBottom: 12 }}>
        <Col>
          <Space wrap>
            <AButton
              type="dashed"
              icon={<KeyOutlined />}
              onClick={() => setModalReset(true)}
              disabled={!professional?.signature_password_hash}
            >
              Resetar senha de assinatura
            </AButton>

            <Popconfirm
              title="Remover assinatura?"
              description="Esta ação apaga assinatura, senha e chaves criptográficas deste profissional."
              okText="Remover"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={handleClearSignature}
              disabled={
                !professional?.signature &&
                !professional?.signature_password_hash
              }
            >
              <AButton
                danger
                icon={<DeleteOutlined />}
                disabled={
                  disabled ||
                  (!professional?.signature &&
                    !professional?.signature_password_hash)
                }
              >
                Remover assinatura cadastrada
              </AButton>
            </Popconfirm>
          </Space>
        </Col>
      </Row>

      {renderSignatureCanvas()}

      <Card
        size="small"
        title={
          <Space size={6}>
            <UploadOutlined style={{ color: '#52c41a' }} />
            <span>Ou faça upload da assinatura (.PNG)</span>
          </Space>
        }
        style={{ marginBottom: 12 }}
      >
        <Typography.Text
          type="secondary"
          style={{ display: 'block', marginBottom: 8, fontSize: 12 }}
        >
          Arquivo PNG com fundo transparente, máximo 100kb. O upload substitui a
          assinatura desenhada acima.
        </Typography.Text>
        <AUpload
          onChange={onChange}
          beforeUpload={beforeUpload}
          colorButton="#32cf5c"
          colorText="#fff"
          listType="picture"
          maxCount={1}
          onRemove={() => setSignatureUploaded(null)}
        />
      </Card>

      <Card
        size="small"
        title={
          <Space size={6}>
            <LockOutlined style={{ color: '#1677ff' }} />
            <span>Configuração de Certificado A1 (Opcional)</span>
          </Space>
        }
        style={{ marginBottom: 12 }}
      >
        <Typography.Text
          type="secondary"
          style={{ display: 'block', marginBottom: 10, fontSize: 12 }}
        >
          Envie seu certificado A1 (.pfx ou .p12) e informe a senha do
          certificado. Quando configurado, novas assinaturas usarão A1 com
          fallback automático para o método atual.
        </Typography.Text>

        <Row gutter={[12, 8]}>
          <Col xs={24} md={12}>
            <AUpload
              label="Upload do certificado A1 (.pfx ou .p12)"
              onChange={onChangeA1}
              beforeUpload={beforeUploadA1}
              listType="text"
              maxCount={1}
              colorButton="#1677ff"
              colorText="#fff"
              onRemove={() => setA1PfxBase64('')}
            />
          </Col>

          <Col xs={24} md={12}>
            <input
              type="password"
              className="ant-input"
              placeholder="Senha do certificado A1"
              value={a1CertificatePassword}
              onChange={(e) => setA1CertificatePassword(e.target.value)}
              style={{
                width: '100%',
                padding: '4px 11px',
                borderRadius: 6,
                border: '1px solid #d9d9d9',
              }}
            />
          </Col>
        </Row>
      </Card>

      {!professional?.signature_password_hash && (
        <Card
          size="small"
          title={
            <Space size={6}>
              <LockOutlined style={{ color: '#faad14' }} />
              <span>Senha de Assinatura</span>
            </Space>
          }
          style={{ marginBottom: 12 }}
        >
          <Typography.Text
            type="secondary"
            style={{ display: 'block', marginBottom: 10, fontSize: 12 }}
          >
            Crie uma senha para proteger e validar sua assinatura digital nos
            documentos gerados pelo sistema.
          </Typography.Text>
          <Form layout="vertical">
            <Row gutter={[12, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={
                    <Space size={6}>
                      <LockOutlined style={{ color: '#8c8c8c' }} />
                      <span>Senha</span>
                    </Space>
                  }
                >
                  <Form.Item noStyle>
                    <input
                      type="password"
                      className="ant-input"
                      placeholder="Crie uma senha para sua assinatura"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '4px 11px',
                        borderRadius: 6,
                        border: '1px solid #d9d9d9',
                      }}
                    />
                  </Form.Item>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={
                    <Space size={6}>
                      <LockOutlined style={{ color: '#8c8c8c' }} />
                      <span>Confirmar Senha</span>
                    </Space>
                  }
                >
                  <Form.Item noStyle>
                    <input
                      type="password"
                      className="ant-input"
                      placeholder="Repita a senha"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '4px 11px',
                        borderRadius: 6,
                        border: '1px solid #d9d9d9',
                      }}
                    />
                  </Form.Item>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
      )}

      <ContainerRow>
        <AButton
          type="default"
          onClick={() => navigate(-1)}
          icon={<LeftCircleOutlined />}
        >
          Voltar
        </AButton>
        <AButton
          type="primary"
          icon={<SaveOutlined />}
          disabled={disabled}
          onClick={handleSignature}
        >
          Salvar Assinatura
        </AButton>
      </ContainerRow>

      {renderModalResetPassword()}
    </ContainerForm>
  );
};

export default ProfessionalSignature;
